import { useState, useEffect } from 'react';
import { api } from '../lib/api';

interface VerifyEmailPageProps {
  token: string;
  onNavigate: (page: string) => void;
  onVerified?: () => void;
}

export default function VerifyEmailPage({ token, onNavigate, onVerified }: VerifyEmailPageProps) {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    api.auth.verifyEmail(token)
      .then((data) => {
        setStatus('success');
        setMessage(data.message || 'Email verified successfully!');
        if (onVerified) onVerified();
        setTimeout(() => onNavigate('welcome'), 2000);
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err.message || 'Verification failed');
      });
  }, [token]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        {status === 'loading' && (
          <>
            <div className="w-10 h-10 border-3 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold">Verifying your email...</h2>
            <p className="text-gray-400 mt-2 text-sm">This will only take a moment.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4 border border-emerald-500/20">
              <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2">Email Verified!</h2>
            <p className="text-gray-400 mb-4">{message}</p>
            <p className="text-sm text-gray-500">Redirecting you to your welcome page...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/20">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2">Verification Failed</h2>
            <p className="text-gray-400 mb-6">{message}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => onNavigate('home')}
                className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-xl font-semibold transition-colors"
              >
                Go Home
              </button>
              <button
                onClick={() => onNavigate('login')}
                className="px-5 py-2.5 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors"
              >
                Sign In
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
